import {
  DeclarationType,
  RequestStatus,
  RequestStatusDetail,
  RequestUserAccessLevel,
} from '../../types';
import { MedicationRouteOfAdministrationRead } from '../../services/request/types';

export interface Resource {
  ['@id']: string;
  ['@type']: string;
}

export interface ActiveStatusTrait {
  active: boolean;
  statusDate?: string;
}

export interface CreateDeleteTrait {
  createdAt: string;
  updatedAt: string;
}

export interface IDeclarationIngredientDetail {
  code: string;
  nameFr: string;
  nameEn: string;
}

interface IDeclarationIngredient {
  activeIngredient: string;
  activeIngredientDetail?: IDeclarationIngredientDetail;
  concentration?: number;
  concentrationUnit?: string;
  qty?: number;
  ordonnanceQc?: boolean;
}

export interface IDeclarationMedicationDetail {
  code: string;
  nameFr: string;
  nameEn: string;
}

interface IDeclarationMedication {
  medication: string;
  medicationDetail?: IDeclarationMedicationDetail;
  concentration?: number;
  concentrationUnit?: string;
  qty?: number;
  ordonnanceQc?: boolean;
}

export interface IIngredientFamily extends Resource, ActiveStatusTrait, CreateDeleteTrait {
  id: number;
  category: string;
  nameFr: string;
  nameEn: string;
}

export interface ISystemProduct {
  ['@id']: string;
  id: number;
  nameEn: string;
  nameFr: string;
  code: string;
  active: boolean;
  statusDate?: string;
  productType: string;
}

export interface Statistic extends Resource {
  name: string;
  values: {
    date: string;
    count: {
      key: string;
      value: number;
    }[];
  }[];
}

export interface PermitType extends Resource {
  id: number;
  code: string;
  nameFr: string;
  nameEn: string;
  name?: string;
  active: boolean;
  createdAt: string;
  isUsed: number;
  statusDate: string;
  updatedAt: string;
}

export interface ICountrySubdivision extends Resource {
  id: number;
  code: string;
  nameFr: string;
  nameEn: string;
  name?: string;
  subdivisionFr: string;
  subdivisionEn: string;
  countryCode: string;
  countryNameFr: string;
  countryNameEn: string;
  countryName?: string;
  active: boolean;
  createdAt: string;
  isUsed: number;
  statusDate: string;
  updatedAt: string;
}

export interface ICoordinate extends Resource {
  address: {
    addressFirst?: string;
    addressSecond?: string;
    unitNumber?: string;
    civicNumber?: string;
    streetName?: string;
    poBox?: string;
    ruralRoute?: string;
    stationInformation?: string;
  };
  city: string;
  postalCode: string;
  countrySubdivision?: string | null;
  country?: string | null;
  unitNumber?: string;
  civicNumber?: string;
  streetName?: string;
  poBox?: string;
  ruralRoute?: string;
  stationInformation?: string;
  addressFirst?: string;
  addressSecond?: string;
  globalAddressValidation?: string;
}

export interface IPermit extends Resource {
  active: boolean;
  createdAt: string;
  operatorEmail?: string;
  id?: number;
  isUsed: number;
  permitNumber: string;
  permitType: string;
  statusDate: string;
  updatedAt: string;
  operatorNumber: number;
  entityNumber: number;
  mapaqPermitNumber: number;
  permitYear: number;
  issueDate?: string;
  expiryDate?: string;
  permitStatus?: string;
  operatorName?: string;
  operatorContact?: string;
  entitySocialReason: string;
  entityCommonName?: string;
  entityCityNumber?: number;
  entityCoordinate?: ICoordinate | null;
  entityManager?: string;
  entityManagerPhoneNumber?: string;
  entityManagerPhoneExtension?: number | null;
  inspectionSection?: number;
  entityType?: number;
  directionNumber?: number;
  directionName?: string;
  regionAdministrativeNumber?: number;
  regionAdministrativeDescription?: string;
  localAdministrationNumber?: number;
  localAdministrationName?: string;
  mrc: string;
  operatorCityNumber?: number;
  operatorCoordinate?: ICoordinate | null;
  operatorPhoneNumber?: string;
  operatorPhoneExtension?: number | null;
  entitySiteNumber?: string;
  ingredientStatus?: string | null;
}

export interface IPermitCode extends Resource {
  permit: string;
  code?: string | null;
  codeType: string;
  active: boolean;
  statusDate?: string;
  createdAt?: string;
  usedDate?: string;
  expiredDate?: string;
  userAccount?: string | any;
}

export interface Declaration extends Resource {
  animalAgeMaxLimit: number;
  animalAgeMinLimit: number;
  animalQty: number;
  animalQtySource: string;
  createdAt: string;
  declarationDate: string;
  declarationPermit: IPermit;
}

export interface Product extends Resource {
  id: number;
  code: string;
  name?: string;
  nameFr: string;
  nameEn: string;
  productType: string;
  isUsed: number;
  active: boolean;
  statusDate: string;
  createdAt: string;
  updatedAt: string;
}

export interface ISpecies extends Resource {
  id: number;
  code: string;
  name?: string;
  nameFr: string;
  nameEn: string;
  officialSite: boolean;
  canHaveSite: boolean;
  vetValidation: boolean;
  isUsed: number;
  active: boolean;
  statusDate: string;
  createdAt: string;
  updatedAt: string;
}

export interface IAnimalCategory extends Resource {
  id: number;
  species: string;
  nameFr: string;
  nameEn: string;
  code: string;
  active: boolean;
  statusDate: string;
  createdAt: string;
  updatedAt: string;
}

// ? Is it necessary ??????
export interface IProductionStageGroup extends Resource {
  id: number;
  name?: string;
  nameFr: string;
  nameEn: string;
  active: boolean;
  statusDate: string;
  createdAt: string;
  updatedAt: string;
}

export interface Medication extends Resource {
  id: number;
  code: string;
  nameFr: string;
  nameEn: string;
  name?: string;
  shortNameFr: string;
  shortNameEn: string;
  shortName?: string;
  active: boolean;
  statusDate: string;
  ordonnanceQc: boolean;
  createdAt: string;
  updatedAt: string;
  isPermitHolder: boolean;
}

export interface ActiveIngredient extends Resource {
  id: number;
  code: string;
  nameFr: string;
  nameEn: string;
  name?: string;
  ordonnanceQc: boolean;
  medicationType: string;
  isUsed: number;
  active: boolean;
  statusDate: string;
  createdAt: string;
  updatedAt: string;
  isPermitHolder: boolean;
}

export interface MonthlyRegisteredPermitStat extends Resource {
  month: string;
  totalPermitsCount: number;
  registeredPermitsCount: number;
  percentage: number;
}

export interface ProductType extends Resource {
  id?: number;
  code?: string;
  nameFr?: string;
  nameEn?: string;
  name?: string;
  isUsed?: number;
  active?: boolean;
  statusDate?: string;
  createdAt?: string;
  updatedAt?: string;
}

export interface IMedication extends Resource {
  code: string;
}

export interface IActiveIngredient extends Resource {
  code: string;
}

export interface IDeclarationRead {
  ['@id']: string;
  id: number;
  declarationDate: string;
  declarationPermit: IPermit;
  declarationType: `${DeclarationType}`;
  sitePermitNumber?: string;
  recipientName: string;
  siteNumber?: string;
  siteName?: string;
  productQty: number;
  productionStage: string;
  productionStageOtherInfo: string;
  animalQtySource: string;
  animalQtySourceOtherInfo?: string | null;
  animalQty: number;
  animalAgeMinLimit?: number;
  animalAgeMaxLimit?: number;
  prescriptionDate?: string | null;
  vetLicenseNumber?: string | null;
  vetName?: string | null;
  recipientCoordinate: ICoordinate;
  siteCoordinate: ICoordinate | null;
  product: string | ISystemProduct;
  providerNumber?: string | null;
  providerNote?: string | null;
  declarationIngredient: IDeclarationIngredient[];
  declarationMedication: IDeclarationMedication[];
}

export interface IDeclarationTemplateMedication extends Resource {
  medication: IMedication | string;
  medicationDetail?: IDeclarationMedicationDetail;
  concentration: number;
  qty: number;
  concentrationUnit: string;
}

export interface IDeclarationTemplateActiveIngredients extends Resource {
  activeIngredient: IActiveIngredient | string;
  activeIngredientDetail?: IDeclarationIngredientDetail;
  concentration: number;
  qty: number;
  concentrationUnit: string;
}

export interface IDeclarationTemplate extends Resource {
  name: string | null;
  description: string | null;
  declarationDate: string;
  permit: IPermit;
  type: string;
  sitePermitNumber: string;
  recipientName: string;
  siteNumber: string;
  siteName: string;
  product: Product;
  productQty: number;
  productionStage: string;
  productionStageOtherInfo: string;
  animalQtySource: string;
  animalQtySourceOtherInfo: string;
  animalQty: number;
  animalAgeMinLimit: number;
  animalAgeMaxLimit: number;
  prescriptionDate: string;
  vetLicenseNumber: string;
  vetName: string;
  recipientCoordinate: ICoordinate;
  siteCoordinate: ICoordinate;
  declarationTemplateActiveIngredients: IDeclarationTemplateActiveIngredients[];
  declarationTemplateMedications: IDeclarationTemplateMedication[];
  providerNumber: string;
  providerNote: string;
  createdAt: string;
  updatedAt: string;
}

export interface IDeclarationTemplateLotRead extends Resource {
  nameFr: string;
  nameEn: string | null;
  descFr: string;
  descEn: string | null;
  declarationTemplates: IDeclarationTemplate[];
}

export interface PermitsWithTransmitionPercentages extends Resource {
  month: string;
  nbPermitsWithOneTransmission: number;
  nbRegisteredPermits: number;
  percentage: number;
}

export interface IUserConsent extends Resource {
  consentType: string;
  authorized: boolean;
}

export interface OmvqVeterinarian extends Resource {
  id: number;
  email: string;
  name: string;
  permitNumber: string;
  omvqEstablishmentVeterinarians: {
    omvqEstablishment: {
      name: string;
    };
  }[];
  omvqStatus: string;
  status: string;
}

export interface OmvqEstablishmentVeterinarians extends Resource {
  linkType: string;
  omvqVeterinarian: OmvqVeterinarian;
}

export interface IVetDeclarationRead extends Resource {
  id: number;
  declarationDate: string | null;
  omvqEstablishment: string;
  declarationType: `${DeclarationType}`;
  vetLicenseNumber: string;
  vetName: string;
  providerNumber: string | null;
  providerNote: string | null;
}

export interface IUserAccount {
  ['@id']?: string;
  id?: number;
  email: string;
  username: string;
  name: string;
  lastName: string;
  language: string;
  active: boolean;
  status: string;
  password: string;
  roles: string[];
  userConsents: {
    authorized: boolean;
    id: string;
    consentType: string;
  }[];
  organization: any;
}

export interface IUserResponse extends IUserAccount {
  ['@context']?: string;
  ['@id']: string;
  ['@type']: string;
  ['hydra:member']: IUserAccount[];
  ['hydra:totalItems']: number;
  ['hydra:view']: any;
}

export interface IHydraView {
  ['@id']: string;
  ['hydra:first']?: string;
  ['hydra:previous']?: string;
  ['hydra:next']?: string;
  ['hydra:last']?: string;
}

export interface IArrayResponse<T> {
  ['@context']: string;
  ['@id']: string;
  ['@type']: string;
  ['hydra:member']: T[] | [];
  ['hydra:view']: IHydraView;
  ['hydra:totalItems']: number;
  ['hydra:search']?: [];
}

export interface IResponse {
  ['@context']: string;
  ['@id']: string;
  ['@type']: string;
}

export interface MedicationType extends Resource {
  active: boolean;
  atcVetCode?: string;
  code?: string;
  createdAt?: string;
  isUsed?: number;
  nameEn?: string;
  nameFr?: string;
  statusDate?: string;
  updatedAt?: string;
}

export enum CanadaProvinceCodes {
  QC = 'CA-QC',
}
export enum OrganizationCodes {
  EPQ = 'EPQ',
}

export interface IOrganizationAuthorizedDomain {
  domain: string | null;
}

export interface IOrganizationDepartment {
  nameFr: string | null;
  nameEn?: string | null;
  code: string;
  id?: number;
}

export interface IOrganizationSpecies {
  id?: number;
  species: ISpecies;
}

export interface IOrganizationSpeciesAdd {
  species: string;
}

export interface IAddOrganization {
  id?: number;
  nameFr: string;
  nameEn?: string;
  coordinate?: ICoordinate;
  phone: string;
  email: string;
  organizationAuthorizedDomains: IOrganizationAuthorizedDomain[];
  organizationDepartments: IOrganizationDepartment[];
  organizationSpecies: IOrganizationSpeciesAdd[];
  active?: boolean;
  code: string;
}

export interface IOrganization {
  ['@id']?: string;
  id?: number;
  nameFr: string;
  nameEn?: string;
  coordinate?: ICoordinate;
  phone: string;
  email: string;
  organizationAuthorizedDomains: IOrganizationAuthorizedDomain[];
  organizationDepartments: IOrganizationDepartment[];
  organizationSpecies: IOrganizationSpecies[];
  active?: boolean;
  code: string;
  name?: string;
}

export interface IPremises extends Resource {
  id: number;
  siteNumber: string;
  farmName: string;
  coordinate: ICoordinate;
}

export interface EpqRequestSite extends Resource {
  id: number;
  number: any;
  /** @deprecated retiré côté backend EPQ V2 — Site n'est plus sous CqaSite */
  epqRequestCqaSite?: {
    name: string;
  };
  owner?: string;
}

export interface EpqRequestCqaSite extends Resource {
  id: number;
  number?: any;
  name: string | null;
  /** @deprecated retiré côté backend EPQ V2 — remplacé par la nav via Building */
  epqRequestSites?: EpqRequestSite[];
}

export interface EpqRequestDivision extends Resource {
  id: number;
  number?: any;
  name: string;
  // eslint-disable-next-line no-use-before-define
  epqRequestBuildingDivisions?: EpqRequestBuildingDivisions[];
  /** @deprecated retiré côté backend EPQ V2 — Division est indépendante */
  epqRequestCqaSites?: EpqRequestCqaSite[];
}

export interface EpqRequestBuilding extends Resource {
  id: number;
  number?: any;
  name?: string | null;
  epqRequestSite?: EpqRequestSite;
  // eslint-disable-next-line no-use-before-define
  epqRequestBuildingDivisions?: EpqRequestBuildingDivisions[];
}

export interface EpqRequestBuildingDivisions extends Resource {
  id: number;
  epqRequestDivision?: EpqRequestDivision;
  epqRequestBuilding?: EpqRequestBuilding;
}

export interface EpqRequestCompany extends Resource {
  id: number;
  number?: any;
  /** @deprecated retiré côté backend EPQ V2 — Company n'a plus de collection Division */
  epqRequestDivisions?: EpqRequestDivision[];
  epqRequestBuildings?: EpqRequestBuilding[];
  name: string;
  ownerContact?: string;
}

export interface EpqRequestUserAccessDetailWrite {
  epqRequestUserAccess: string;
  epqRequestSite: string | null;
  epqRequestDivision: string | null;
}

export interface EpqRequestUserAccessDetailRead {
  ['@id']: string;
  id: number;
  epqRequestUserAccess: string;
  epqRequestSite: {
    ['@id']?: string;
    id: number;
    number: string;
  } | null;
  epqRequestDivision: {
    ['@id']?: string;
    id: number;
    number: string;
    name: string;
    epqRequestBuildings?: EpqRequestBuilding[];
  } | null;
}

export interface EpqRequestUserAccessWrite {
  id?: number;
  accessLevel: string | RequestUserAccessLevel;
  accessCode: string;
  accessCodeExpiryDate: string;
  active: boolean;
  userAccount: string | null;
  epqAccessRequest: string;
  epqRequestUserAccessDetails: EpqRequestUserAccessDetailWrite[];
  omvqVeterinarian: string | null;
}

export interface EpqRequestUserAccessRead
  extends Omit<
      EpqRequestUserAccessWrite,
      'id' | 'userAccount' | 'omvqVeterinarian' | 'epqAccessRequest' | 'epqRequestUserAccessDetails'
    >,
    Resource {
  id: number;
  userAccount: {
    ['@id']: string;
    name: string;
    username: string;
  };
  createdBy: {
    ['@id']: string;
    name: string;
    username: string;
  };
  epqAccessRequest: {
    organizationProducerNumber?: string;
  };
  omvqVeterinarian: {
    ['@id']: string;
    email?: string;
    name?: string;
  };
  epqRequestUserAccessDetails: EpqRequestUserAccessDetailRead[];
}

export interface EpqRequestUserAccess extends Resource {
  id: number;
  accessLevel: string | RequestUserAccessLevel;
  accessCode: string;
  accessCodeExpiryDate: string;
  active: boolean;
  userAccount: {
    ['@id']?: string;
    name: string;
    username: string;
  };
  epqAccessRequest: {
    organizationProducerNumber?: string;
  };
  epqRequestUserAccessDetails: EpqRequestUserAccessDetailRead[];
  omvqVeterinarian: {
    ['@id']?: string;
    email?: string;
    name?: string;
    permitNumber?: string;
  } | null;
}

export interface EpqAccessRequest extends Resource {
  id?: number;
  createdBy: IUserAccount;
  ownerFullName?: string;
  operatorNumber?: string;
  authorisationCode?: string;
  organizationProducerNumber?: string;
  requestStatusDate?: string;
  organization: any;
  organizationDepartment: any;
  identificationConsent?: boolean;
  monitoringConsent?: boolean;
  /** @deprecated backend EPQ V2 renvoie epqRequestCompany (scalaire OneToOne) */
  epqRequestCompanies?: EpqRequestCompany[];
  epqRequestCompany?: EpqRequestCompany;
  epqRequestUserAccesses: EpqRequestUserAccess[];
  requestStatus: RequestStatus;
  requestStatusDetail?: RequestStatusDetail;
}

export interface ISystemTypeBase {
  ['@id']: string;
  id: number;
  nameEn: string;
  nameFr: string;
  code: string;
  active: boolean;
  statusDate?: string;
}

export interface ISystemMedicationType extends ISystemTypeBase {
  atcVetCode?: string | null;
  familyCode?: string | null;
  familyFr?: string | null;
  codeFmv?: string | null;
}

export interface ISystemActiveIngredientMedicationType {
  activeIngredient?: string | null;
  medicationType: string | null;
}

export interface ISystemActiveIngredient extends ISystemTypeBase {
  codeAi?: string | null;
  medicationType: any;
  ordonnanceQc: boolean;
  activeIngredientMedicationTypes: ISystemActiveIngredientMedicationType[];
  isPermitHolder: boolean;
}

export interface MedicationActiveIngredientWrite {
  activeIngredient: string | null;
  concentration: number | null;
  unit: string | null;
  standardConcentration: number | null;
  standardConcentrationUnit: string | null;
}

export interface MedicationActiveIngredientRead {
  ['@id']: string;
  id: number;
  activeIngredient: ISystemActiveIngredient;
  concentration: number | null;
  unit: string | null;
  standardConcentration: number | null;
  standardConcentrationUnit: string | null;
}

export interface ISystemActiveIngredientPharmaceuticalForm {
  ['@id']: string;
  activeIngredient: ISystemActiveIngredient;
  nameEn: string;
  nameFr: string;
  active: boolean;
}

export interface ISystemIngredientFamily {
  ['@id']: string;
  nameEn: string;
  nameFr: string;
  category: number | null;
  active: boolean;
}

export interface ISystemMedicationActiveIngredientFormula {
  activeIngredient: string | null;
  concentration: number | null;
  unit: string | null;
  activeIngredientPharmaceuticalForm: string | null;
  ingredientFamily: string | null;
}

export interface ISystemMedicationActiveIngredientFormulaRead {
  ['@id']: string;
  id: number;
  activeIngredient: ISystemActiveIngredient;
  concentration: number | null;
  unit: string | null;
  activeIngredientPharmaceuticalForm: ISystemActiveIngredientPharmaceuticalForm;
  ingredientFamily: ISystemIngredientFamily;
}

export interface ISystemMedicationDin {
  dinNumber?: string;
  startDate?: string | null;
  endDate?: string | null;
}

export interface MedicationRouteOfAdministrationForm {
  routeOfAdministration: string | null;
}

export interface ISystemMedication extends ISystemTypeBase {
  drugCode: string;
  validationStatus: string;
  source?: string;
  shortNameFr: string;
  shortNameEn: string;
  note?: string;
  medicationActiveIngredients: MedicationActiveIngredientWrite[];
  medicationActiveIngredientFormulas: ISystemMedicationActiveIngredientFormula[];
  medicationDins: ISystemMedicationDin[];
  medicationRouteOfAdministrations: MedicationRouteOfAdministrationForm[];
  isPermitHolder: boolean;
}

export interface ISystemMedicationRead extends ISystemTypeBase {
  drugCode: string;
  validationStatus: string;
  source?: string;
  shortNameFr: string;
  shortNameEn: string;
  note?: string;
  medicationActiveIngredients: MedicationActiveIngredientRead[];
  medicationActiveIngredientFormulas: ISystemMedicationActiveIngredientFormulaRead[];
  medicationDins: ISystemMedicationDin[];
  medicationRouteOfAdministrations: MedicationRouteOfAdministrationRead[];
  isPermitHolder: boolean;
}

export interface ISystemProductionStage extends ISystemTypeBase {
  products: string[];
  species: any;
  productionStageGroup: any;
  unitLimit: string;
  precision: boolean;
  isUsed?: boolean;
  canHaveSite?: boolean | null;
  officialSite?: boolean | null;
}

export interface IUpdateSystemTypeBase {
  data: any;
  iri: string;
}

export interface IOmvqVeterinarianSkeleton {
  ['@id']: string;
  id: number;
  permitNumber: string;
  name: string;
  email: string;
  omvqStatus: string;
  omvqStartDate?: Date | null;
  omvqEndDate?: Date | null;
  areaOfPractice?: any[];
  status: string;
  statusDate: Date;
  createdAt: Date;
  updatedAt: Date;
}

export interface IOmvqEstablishmentSkeleton {
  ['@id']: string;
  id: number;
  name: string;
  address: string;
  city: string;
  province: string;
  country: string;
  postalCode: string;
  phone: string;
  active: boolean;
  statusDate: Date;
  createdAt: Date;
  updatedAt: Date;
}

export interface IOmvqEstablishmentVeterinarianSkeleton {
  ['@id']: string;
  id: number;
  linkType: string;
}

export interface IOmvqEstablishmentVeterinarianVeterinarian
  extends IOmvqEstablishmentVeterinarianSkeleton {
  omvqVeterinarian: IOmvqVeterinarianSkeleton;
}

export interface IOmvqEstablishmentVeterinarianEstablishment
  extends IOmvqEstablishmentVeterinarianSkeleton {
  omvqEstablishment: IOmvqEstablishmentSkeleton;
}

export interface IOmvqVeterinarian extends IOmvqVeterinarianSkeleton {
  omvqEstablishmentVeterinarians: IOmvqEstablishmentVeterinarianEstablishment[];
  omvqVeterinarianHistories: {
    obtainedDate?: Date | null;
    terminationDate?: Date | null;
    revocationDate?: Date | null;
  }[];
  licenseNumber: string;
}

export interface IOmvqEstablishment extends IOmvqEstablishmentSkeleton {
  omvqEstablishmentVeterinarians: IOmvqEstablishmentVeterinarianVeterinarian[];
}
